/**
 * A person's avatar (M72): their photo when they have one, else their
 * initials. The photo comes from the one cached list of every user's photo
 * (hooks/useUserAvatars.ts), so a page full of avatars costs one request.
 * A photo that fails to load (e.g. its signed URL expired) falls back to the
 * initials. The Listings screens wrap it in their own styling
 * (components/listings/parts.tsx Avatar); PersonCard.tsx adds the person's
 * name and contact details beside it.
 */
import { useState } from 'react';
import { useUserAvatarUrl } from '../../hooks/useUserAvatars';

/** "AM" for "Alex Morgan"; "?" for nobody. */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/**
 * The photo to show for `userId` (or the explicit `src`), and the <img>
 * onError that drops it for the initials. null = show the initials.
 */
export function useAvatarImage(userId: string | null | undefined, src?: string | null) {
  const fetched = useUserAvatarUrl(userId);
  const url = src ?? fetched;
  const [failed, setFailed] = useState<string | null>(null);
  return { url: url && url !== failed ? url : null, onError: () => setFailed(url) };
}

export interface UserAvatarProps {
  userId?: string | null;
  name: string | null | undefined;
  /** An explicit photo URL; else the user's photo by `userId`. */
  src?: string | null;
  /** Size + text size, e.g. 'w-8 h-8 text-xs' (the default). */
  className?: string;
  /** The accessible name when `name` is empty (default 'Unknown user'). */
  emptyLabel?: string;
}

export function UserAvatar({ userId, name, src, className = 'w-8 h-8 text-xs', emptyLabel = 'Unknown user' }: UserAvatarProps) {
  const image = useAvatarImage(userId, src);
  const label = name || emptyLabel;
  if (image.url) {
    return <img src={image.url} alt={label} onError={image.onError} className={`${className} shrink-0 rounded-full object-cover`} />;
  }
  return (
    <span
      role="img"
      aria-label={label}
      className={`${className} inline-flex shrink-0 select-none items-center justify-center rounded-full bg-gray-200 font-semibold text-gray-700`}
    >
      {initials(name)}
    </span>
  );
}
